import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Alert,
  CircularProgress,
  Skeleton
} from '@mui/material'
import {
  Clear as ClearIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { enGB } from 'date-fns/locale'
import { useAuth } from '../../../../auth/useAuth'
import { useTableSort } from '../../../../hooks/useTableSort'
import SortableTableHeader from '../../../../components/SortableTableHeader/SortableTableHeader'

// Types
interface Identifier {
  Id: number
  Entity_Type: string
  Identifier_Type: string
  Entity_Name: string
  Identifier_Value: string
  Asof: string | null
  Timestamp: string | null
  Deleted: boolean
}

interface IdentifierFormData {
  Entity_Type: string
  Identifier_Type: string
  Entity_Name: string
  Identifier_Value: string
  Asof: Date | null
}

interface PaginationInfo {
  page: number
  limit: number
  total_count: number
  total_pages: number
  has_next: boolean
  has_prev: boolean
}

interface PaginatedResponse {
  data: Identifier[]
  pagination: PaginationInfo
}

const initialFormData: IdentifierFormData = {
  Entity_Type: '',
  Identifier_Type: '',
  Entity_Name: '',
  Identifier_Value: '',
  Asof: null
}

const IdentifiersManagement: React.FC = () => {
  const { user, getAccessToken } = useAuth()
  const [identifiers, setIdentifiers] = useState<Identifier[]>([])
  const [loading, setLoading] = useState(false)
  const [backgroundLoading, setBackgroundLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Pagination state
  const [totalCount, setTotalCount] = useState(0)
  const [loadedCount, setLoadedCount] = useState(0)
  const [currentBackendPage, setCurrentBackendPage] = useState(1)
  const [hasMoreData, setHasMoreData] = useState(true)
  const [isInitialLoad, setIsInitialLoad] = useState(true)

  // Form state (drives table filtering)
  const [formData, setFormData] = useState<IdentifierFormData>(initialFormData)

  // Table state
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  // Dropdown options state
  const [entityTypes, setEntityTypes] = useState<string[]>([])
  const [identifierTypes, setIdentifierTypes] = useState<string[]>([])
  const [dropdownsLoading, setDropdownsLoading] = useState(false)

  // Load initial identifiers data with pagination
  const loadInitialIdentifiers = async () => {
    setLoading(true)
    setError(null)
    setIsInitialLoad(true)

    try {
      const token = await getAccessToken()

      if (!token) {
        throw new Error('No access token available')
      }

      // First get total count
      const countResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/identifiers/?count_only=true`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!countResponse.ok) {
        throw new Error('Failed to get identifiers count')
      }

      const countData = await countResponse.json()
      setTotalCount(countData.total_count)

      // Then load first page (100 records)
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/identifiers/?page=1&limit=100`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error('Failed to load identifiers')
      }

      const data: PaginatedResponse = await response.json()

      // Process identifiers from API response
      const processedIdentifiers = data.data.map(identifier => ({
        ...identifier,
        Asof: identifier.Asof || null,
        Timestamp: identifier.Timestamp || null
      }))

      setIdentifiers(processedIdentifiers)
      setLoadedCount(processedIdentifiers.length)
      setCurrentBackendPage(1)
      setHasMoreData(data.pagination.has_next)

      // Start background loading if there's more data
      if (data.pagination.has_next && countData.total_count > 100) {
        setTimeout(() => loadMoreIdentifiersInBackground(countData.total_count), 1000)
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load identifiers')
      const sampleIdentifiers = [
        {
          Id: 1,
          Entity_Type: 'Fund',
          Identifier_Type: 'ISIN',
          Entity_Name: 'Sample Fund',
          Identifier_Value: 'US1234567890',
          Asof: null,
          Timestamp: null,
          Deleted: false
        }
      ]
      setIdentifiers(sampleIdentifiers)
      setTotalCount(1)
      setLoadedCount(1)
    } finally {
      setLoading(false)
      setIsInitialLoad(false)
    }
  }

  // Load all remaining identifiers in background
  const loadMoreIdentifiersInBackground = async (totalRecords: number) => {
    if (!hasMoreData || backgroundLoading) return

    setBackgroundLoading(true)

    try {
      const token = await getAccessToken()

      if (!token) {
        return
      }

      // Calculate how many records remain
      const remainingRecords = totalRecords - loadedCount
      if (remainingRecords <= 0) {
        setHasMoreData(false)
        return
      }

      // Load all remaining records in one request using page=1 with a large limit
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/identifiers/?page=1&limit=${totalRecords}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        return
      }

      const data: PaginatedResponse = await response.json()

      // Process identifiers from API response
      const processedIdentifiers = data.data.map(identifier => ({
        ...identifier,
        Asof: identifier.Asof || null,
        Timestamp: identifier.Timestamp || null
      }))

      // Replace all identifiers with the complete dataset
      setIdentifiers(processedIdentifiers)
      setLoadedCount(processedIdentifiers.length)
      setHasMoreData(false) // All data loaded

    } catch (err) {
      // Silently fail background loading
      console.warn('Background loading failed:', err)
    } finally {
      setBackgroundLoading(false)
    }
  }

  // Load dropdown options
  const loadDropdownOptions = async () => {
    setDropdownsLoading(true)

    try {
      const token = await getAccessToken()

      if (!token) {
        setEntityTypes(['Fund', 'Issuer', 'PositionID', 'PositionIssuerID'])
        setIdentifierTypes(['BEZ', 'CUSIP', 'ISIN', 'LEI'])
        return
      }

      // Load entity types
      const entityTypesResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/identifiers/entity-types`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (entityTypesResponse.ok) {
        const entityTypesData = await entityTypesResponse.json()
        setEntityTypes(entityTypesData)
      } else {
        setEntityTypes(['Fund', 'Issuer', 'PositionID', 'PositionIssuerID'])
      }

      // Load identifier types
      const identifierTypesResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/v1/strategic/identifiers/identifier-types`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (identifierTypesResponse.ok) {
        const identifierTypesData = await identifierTypesResponse.json()
        setIdentifierTypes(identifierTypesData)
      } else {
        setIdentifierTypes(['BEZ', 'CUSIP', 'ISIN', 'LEI'])
      }

    } catch (err) {
      setEntityTypes(['Fund', 'Issuer', 'PositionID', 'PositionIssuerID'])
      setIdentifierTypes(['BEZ', 'CUSIP', 'ISIN', 'LEI'])
    } finally {
      setDropdownsLoading(false)
    }
  }

  // Refresh all data
  const refreshIdentifiers = () => {
    setIdentifiers([])
    setTotalCount(0)
    setLoadedCount(0)
    setCurrentBackendPage(1)
    setHasMoreData(true)
    loadInitialIdentifiers()
  }

  useEffect(() => {
    loadDropdownOptions()
    loadInitialIdentifiers()
  }, [])

  // Handle form changes (also filters the table in real-time)
  const handleInputChange = (field: keyof IdentifierFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    setPage(0)
  }

  // Handle clear filters
  const handleClear = () => {
    setFormData(initialFormData)
  }

  // Check if any form fields have values (used for filtering indication)
  const hasFormCriteria = formData.Entity_Type || formData.Identifier_Type || formData.Entity_Name || formData.Identifier_Value

  // Filter identifiers based on form data (form doubles as filter)
  const filteredIdentifiers = identifiers.filter(identifier => {
    if (formData.Entity_Type && identifier.Entity_Type !== formData.Entity_Type) return false
    if (formData.Identifier_Type && identifier.Identifier_Type !== formData.Identifier_Type) return false
    if (formData.Entity_Name && !identifier.Entity_Name.toLowerCase().includes(formData.Entity_Name.toLowerCase())) return false
    if (formData.Identifier_Value && !identifier.Identifier_Value.toLowerCase().includes(formData.Identifier_Value.toLowerCase())) return false
    return true
  })

  // Apply sorting to filtered identifiers
  const { sortedData: sortedIdentifiers, handleSort, getSortDirection, getSortIndex } = useTableSort({
    data: filteredIdentifiers,
    initialSort: [{ field: 'Entity_Name', direction: 'asc' }]
  })

  // Paginated identifiers
  const paginatedIdentifiers = sortedIdentifiers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  )

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <Box>
        <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 'bold' }}>
          Identifiers Management
        </Typography>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          {/* Add / Edit Form (also filters the table as you type) */}
          <Grid item xs={12} data-form-section>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Filter Identifiers
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Entity Type</InputLabel>
                    <Select
                      value={formData.Entity_Type}
                      label="Entity Type"
                      onChange={(e) => handleInputChange('Entity_Type', e.target.value)}
                    >
                      <MenuItem value="">
                        <em>All</em>
                      </MenuItem>
                      {entityTypes.map(type => (
                        <MenuItem key={type} value={type}>{type}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Entity Name"
                    value={formData.Entity_Name}
                    onChange={(e) => handleInputChange('Entity_Name', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Identifier Type</InputLabel>
                    <Select
                      value={formData.Identifier_Type}
                      label="Identifier Type"
                      onChange={(e) => handleInputChange('Identifier_Type', e.target.value)}
                    >
                      <MenuItem value="">
                        <em>All</em>
                      </MenuItem>
                      {identifierTypes.map(type => (
                        <MenuItem key={type} value={type}>{type}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Identifier Value"
                    value={formData.Identifier_Value}
                    onChange={(e) => handleInputChange('Identifier_Value', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <DatePicker
                    label="As Of"
                    value={formData.Asof}
                    onChange={(newValue) => handleInputChange('Asof', newValue)}
                    slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={<ClearIcon />}
                      onClick={handleClear}
                      disabled={loading || !hasFormCriteria}
                      size="small"
                    >
                      Clear Filters
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Table Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Box>
                  <Typography variant="h6">
                    Identifiers List ({filteredIdentifiers.length}{hasFormCriteria ? ` of ${totalCount}` : ''})
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <IconButton onClick={refreshIdentifiers} disabled={loading}>
                    {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                  </IconButton>
                </Box>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <SortableTableHeader
                        field="Entity_Type"
                        label="Entity Type"
                        sortDirection={getSortDirection('Entity_Type')}
                        sortIndex={getSortIndex('Entity_Type')}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        field="Entity_Name"
                        label="Entity Name"
                        sortDirection={getSortDirection('Entity_Name')}
                        sortIndex={getSortIndex('Entity_Name')}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        field="Identifier_Type"
                        label="Identifier Type"
                        sortDirection={getSortDirection('Identifier_Type')}
                        sortIndex={getSortIndex('Identifier_Type')}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        field="Identifier_Value"
                        label="Identifier Value"
                        sortDirection={getSortDirection('Identifier_Value')}
                        sortIndex={getSortIndex('Identifier_Value')}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        field="Asof"
                        label="As Of"
                        sortDirection={getSortDirection('Asof')}
                        sortIndex={getSortIndex('Asof')}
                        onSort={handleSort}
                      />
                      <SortableTableHeader
                        field="Timestamp"
                        label="Timestamp"
                        sortDirection={getSortDirection('Timestamp')}
                        sortIndex={getSortIndex('Timestamp')}
                        onSort={handleSort}
                      />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading && identifiers.length === 0 ? (
                      Array.from({ length: rowsPerPage }).map((_, index) => (
                        <TableRow key={`skeleton-${index}`}>
                          <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                          <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                          <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                          <TableCell><Skeleton variant="text" width="90%" /></TableCell>
                          <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                          <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                        </TableRow>
                      ))
                    ) : paginatedIdentifiers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No identifiers found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedIdentifiers.map((identifier) => (
                        <TableRow key={identifier.Id}>
                          <TableCell>{identifier.Entity_Type}</TableCell>
                          <TableCell>{identifier.Entity_Name}</TableCell>
                          <TableCell>{identifier.Identifier_Type}</TableCell>
                          <TableCell>{identifier.Identifier_Value}</TableCell>
                          <TableCell>
                            {identifier.Asof ? new Date(identifier.Asof).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell>
                            {identifier.Timestamp ? new Date(identifier.Timestamp).toLocaleString() : '-'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredIdentifiers.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10))
                  setPage(0)
                }}
              />
            </Paper>
          </Grid>
        </Grid>

      </Box>
    </LocalizationProvider>
  )
}

export default IdentifiersManagement
